<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  automationId: {
    type: [String, Number],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const isLoading = ref(false);
const executions = ref([]);
const meta = ref({ total: 0, limit: 50, offset: 0 });
const activeFilter = ref('all');
const expandedId = ref(null);
const autoRefresh = ref(false);
let autoRefreshTimer = null;

const STATUS_FILTERS = computed(() => [
  {
    value: 'all',
    label: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.FILTER_ALL'),
  },
  {
    value: 'completed',
    label: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.FILTER_SUCCESS'),
  },
  {
    value: 'failed',
    label: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.FILTER_FAILED'),
  },
  {
    value: 'running',
    label: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.FILTER_RUNNING'),
  },
]);

const loadExecutions = async (append = false) => {
  isLoading.value = true;
  try {
    const params = {
      limit: 50,
      offset: append ? executions.value.length : 0,
    };
    // Filter on the server so total/empty-state/load-more stay correct across
    // pages (audit P2-7); 'all' sends no status filter.
    if (activeFilter.value !== 'all') params.status = activeFilter.value;
    const data = await store.dispatch('automationFlow/fetchExecutions', {
      automationId: props.automationId,
      params,
    });
    if (append) {
      executions.value = [...executions.value, ...(data.executions || [])];
    } else {
      executions.value = data.executions || [];
    }
    meta.value = data.meta || { total: 0, limit: 50, offset: 0 };
  } catch {
    // silent fail — user sees empty state
  } finally {
    isLoading.value = false;
  }
};

const loadMore = () => loadExecutions(true);

const setFilter = filter => {
  if (activeFilter.value === filter) return;
  activeFilter.value = filter;
  // Re-query from the first page so the server applies the new status filter.
  loadExecutions(false);
};

const toggleExpand = id => {
  expandedId.value = expandedId.value === id ? null : id;
};

const startAutoRefresh = () => {
  autoRefreshTimer = setInterval(() => loadExecutions(false), 30000);
};

const stopAutoRefresh = () => {
  if (autoRefreshTimer) {
    clearInterval(autoRefreshTimer);
    autoRefreshTimer = null;
  }
};

const toggleAutoRefresh = () => {
  autoRefresh.value = !autoRefresh.value;
  if (autoRefresh.value) {
    startAutoRefresh();
  } else {
    stopAutoRefresh();
  }
};

const statusBadgeClass = status => {
  // NooviChat design tokens (teal/ruby/amber) — they carry their own dark-mode
  // overrides via _next-colors.scss, so no `dark:` variants needed. Mirrors
  // the status colors used on the automation cards in the listing.
  const classes = {
    completed: 'bg-n-teal-3 text-n-teal-11',
    // completed but a node errored mid-flow — amber, not a clean teal.
    completed_error: 'bg-n-amber-3 text-n-amber-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    running: 'bg-n-amber-3 text-n-amber-11',
    pending: 'bg-n-alpha-2 text-n-slate-11',
    cancelled: 'bg-n-alpha-2 text-n-slate-11',
  };
  return classes[status] || 'bg-n-alpha-2 text-n-slate-11';
};

const statusLabel = status => {
  const labels = {
    completed: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.STATUS_SUCCESS'),
    completed_error: t(
      'PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.STATUS_COMPLETED_WITH_ERROR'
    ),
    failed: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.STATUS_FAILED'),
    running: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.STATUS_RUNNING'),
    pending: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.STATUS_PENDING'),
    cancelled: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.STATUS_CANCELLED'),
  };
  return labels[status] || status;
};

// An execution can complete overall while a node failed mid-flow (continue-on-
// error). Surface that as a distinct amber state instead of a clean "Success".
const hasNodeError = execution =>
  execution.status === 'completed' && !!execution.failed_node_id;
const displayStatus = execution =>
  hasNodeError(execution) ? 'completed_error' : execution.status;

// Resolve a failing node id to a human label using the currently loaded flow.
const flowNodes = computed(() => store.state.automationFlow?.flow?.nodes || []);
const nodeLabelFor = id => {
  if (!id) return '';
  const node = flowNodes.value.find(n => n.id === id);
  return node?.data?.label || node?.data?.type || id;
};

const logStatusDotClass = status => {
  if (status === 'error' || status === 'failed') return 'bg-n-ruby-9';
  if (status === 'success' || status === 'completed') return 'bg-n-teal-9';
  if (status === 'running') return 'bg-n-amber-9';
  return 'bg-n-slate-7';
};

const formatTriggerEvent = event => {
  if (!event) return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EVENT_MANUAL');
  const labels = {
    pipeline_item_created: t(
      'PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EVENT_CARD_CREATED'
    ),
    pipeline_item_updated: t(
      'PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EVENT_CARD_UPDATED'
    ),
    pipeline_item_stage_changed: t(
      'PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EVENT_STAGE_CHANGED'
    ),
    pipeline_item_won: t(
      'PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EVENT_CARD_WON'
    ),
    pipeline_item_lost: t(
      'PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EVENT_CARD_LOST'
    ),
    conversation_created: t(
      'PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EVENT_CONVERSATION_CREATED'
    ),
    message_created: t(
      'PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EVENT_MESSAGE_CREATED'
    ),
    manual: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EVENT_MANUAL'),
    scheduled: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EVENT_SCHEDULED'),
    webhook: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EVENT_WEBHOOK'),
    dry_run: t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EVENT_DRY_RUN'),
  };
  return labels[event] || event;
};

const formatRelativeTime = iso => {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1)
    return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.TIME_JUST_NOW');
  if (diffMins < 60)
    return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.TIME_MINUTES_AGO', {
      count: diffMins,
    });
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24)
    return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.TIME_HOURS_AGO', {
      count: diffHours,
    });
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7)
    return t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.TIME_DAYS_AGO', {
      count: diffDays,
    });
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
};

const formatLogTime = iso => {
  if (!iso) return '';
  const date = new Date(iso);
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

onMounted(() => {
  if (props.automationId) loadExecutions(false);
});

onUnmounted(() => {
  stopAutoRefresh();
});
</script>

<template>
  <div class="flex flex-col h-full overflow-hidden bg-n-background">
    <!-- Toolbar -->
    <div
      class="flex items-center justify-between px-4 py-2.5 border-b border-n-weak flex-shrink-0 bg-n-solid-2"
    >
      <!-- Status filters -->
      <div class="flex items-center gap-1">
        <button
          v-for="filter in STATUS_FILTERS"
          :key="filter.value"
          class="px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          :class="[
            activeFilter === filter.value
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2',
          ]"
          @click="setFilter(filter.value)"
        >
          {{ filter.label }}
        </button>
      </div>

      <!-- Refresh controls -->
      <div class="flex items-center gap-2">
        <button
          class="px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          :class="[
            autoRefresh
              ? 'bg-n-brand/10 text-n-brand border border-n-brand/20'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2',
          ]"
          @click="toggleAutoRefresh"
        >
          {{ t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.AUTO_REFRESH') }}
        </button>
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :disabled="isLoading"
          @click="loadExecutions(false)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            :class="{ 'animate-spin': isLoading }"
          >
            <path d="M21 2v6h-6" />
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M3 22v-6h6" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
          {{ t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.REFRESH') }}
        </button>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div
      v-if="isLoading && executions.length === 0"
      class="flex items-center justify-center flex-1"
    >
      <div class="flex flex-col items-center gap-2 text-n-slate-10">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="animate-spin"
        >
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
        <span class="text-sm">{{
          t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.LOADING')
        }}</span>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!isLoading && executions.length === 0"
      class="flex flex-col items-center justify-center flex-1 px-8 text-center"
    >
      <div
        class="w-12 h-12 rounded-full bg-n-alpha-2 flex items-center justify-center mb-4"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-9"
        >
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </svg>
      </div>
      <p class="text-sm font-medium text-n-slate-12 mb-1">
        {{ t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EMPTY_TITLE') }}
      </p>
      <p class="text-xs text-n-slate-10 max-w-xs leading-relaxed">
        {{
          activeFilter === 'all'
            ? t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EMPTY_DESCRIPTION_ALL')
            : t(
                'PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EMPTY_DESCRIPTION_FILTERED'
              )
        }}
      </p>
    </div>

    <!-- Executions list -->
    <div v-else class="flex-1 overflow-y-auto">
      <div class="divide-y divide-n-weak">
        <div
          v-for="execution in executions"
          :key="execution.id"
          class="hover:bg-n-alpha-1 transition-colors"
        >
          <!-- Summary row -->
          <button
            class="w-full flex items-center gap-3 px-4 py-3 text-left"
            @click="toggleExpand(execution.id)"
          >
            <!-- Status badge (amber "completed with error" when a node failed) -->
            <span
              :class="statusBadgeClass(displayStatus(execution))"
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0 min-w-[60px] justify-center"
            >
              {{ statusLabel(displayStatus(execution)) }}
            </span>

            <!-- Relative time -->
            <span class="text-xs text-n-slate-11 shrink-0 min-w-[70px]">
              {{
                formatRelativeTime(execution.started_at || execution.created_at)
              }}
            </span>

            <!-- Trigger event -->
            <span
              class="text-xs text-n-slate-10 shrink-0 bg-n-alpha-2 px-2 py-0.5 rounded-full"
            >
              {{ formatTriggerEvent(execution.trigger_event) }}
            </span>

            <!-- Spacer -->
            <span class="flex-1" />

            <!-- Node count -->
            <span class="text-xs text-n-slate-10 shrink-0">
              {{
                t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.NODES_COUNT', {
                  count: execution.nodes_executed || 0,
                })
              }}
            </span>

            <!-- Execution time -->
            <span
              v-if="execution.execution_time_ms != null"
              class="text-xs font-mono text-n-slate-10 shrink-0 min-w-[50px] text-right"
            >
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
              {{ execution.execution_time_ms }}ms
            </span>

            <!-- Expand chevron -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-n-slate-9 shrink-0 transition-transform duration-150"
              :class="expandedId === execution.id ? 'rotate-90' : ''"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>

          <!-- Expanded details -->
          <div
            v-if="expandedId === execution.id"
            class="px-4 pb-4 space-y-3 border-t border-n-weak bg-n-solid-1"
          >
            <!-- Error message (also shown for completed runs where a node failed) -->
            <div
              v-if="execution.error_message || execution.failed_node_id"
              class="mt-3 rounded-lg bg-n-ruby-2 border border-n-ruby-5 p-3"
            >
              <div class="flex items-center gap-1.5 mb-1.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-n-ruby-9 shrink-0"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span class="text-xs font-semibold text-n-ruby-11">
                  {{
                    t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EXECUTION_ERROR')
                  }}
                </span>
              </div>
              <p
                v-if="execution.failed_node_id"
                class="text-xs font-medium text-n-ruby-11 mb-1"
              >
                {{
                  t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.FAILED_AT_NODE', {
                    node: nodeLabelFor(execution.failed_node_id),
                  })
                }}
              </p>
              <pre
                class="text-xs text-n-ruby-11 font-mono whitespace-pre-wrap break-all leading-relaxed"
                >{{
                  execution.error_message || execution.failed_node_error
                }}</pre
              >
            </div>

            <!-- Context info -->
            <div
              v-if="
                execution.conversation_id ||
                execution.contact_id ||
                execution.pipeline_item_id
              "
              class="mt-3 flex flex-wrap gap-2"
            >
              <span
                v-if="execution.conversation_id"
                class="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-n-alpha-2 text-xs text-n-slate-11"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                  />
                </svg>
                {{
                  t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.CONVERSATION')
                }}#{{ execution.conversation_id }}
              </span>
              <span
                v-if="execution.contact_id"
                class="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-n-alpha-2 text-xs text-n-slate-11"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                {{ t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.CONTACT') }} #{{
                  execution.contact_id
                }}
              </span>
              <span
                v-if="execution.pipeline_item_id"
                class="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-n-alpha-2 text-xs text-n-slate-11"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                </svg>
                {{ t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.CARD') }} #{{
                  execution.pipeline_item_id
                }}
              </span>
            </div>

            <!-- Execution logs -->
            <div
              v-if="execution.logs && execution.logs.length > 0"
              class="mt-3"
            >
              <p
                class="text-xs font-medium text-n-slate-11 mb-2 uppercase tracking-wide"
              >
                {{
                  t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.EXECUTION_LOGS')
                }}
              </p>
              <div
                class="rounded-lg border border-n-weak overflow-hidden font-mono text-xs"
              >
                <div
                  v-for="(log, idx) in execution.logs"
                  :key="idx"
                  class="flex items-start gap-2.5 px-3 py-2"
                  :class="[
                    idx > 0 ? 'border-t border-n-weak' : '',
                    log.status === 'error' || log.status === 'failed'
                      ? 'bg-n-ruby-2'
                      : 'bg-n-solid-1',
                  ]"
                >
                  <span
                    :class="logStatusDotClass(log.status)"
                    class="mt-1.5 shrink-0 w-2 h-2 rounded-full"
                  />
                  <div class="flex-1 min-w-0">
                    <span class="text-n-slate-12 font-medium">{{
                      log.node_type ||
                      log.node_id ||
                      t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.NODE_LABEL')
                    }}</span>
                    <span
                      v-if="log.node_id && log.node_type"
                      class="text-n-slate-9 ml-1 text-[10px]"
                    >
                      ({{ log.node_id }})
                    </span>
                    <span v-if="log.message" class="text-n-slate-10 ml-1">
                      — {{ log.message }}
                    </span>
                  </div>
                  <span
                    v-if="log.timestamp"
                    class="text-n-slate-9 shrink-0 text-[10px] mt-0.5"
                  >
                    {{ formatLogTime(log.timestamp) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- No logs available -->
            <div
              v-else-if="!execution.error_message"
              class="mt-3 text-xs text-n-slate-9 italic"
            >
              {{ t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.NO_LOGS') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Load more -->
      <div
        v-if="meta.total > executions.length"
        class="flex justify-center py-4 border-t border-n-weak"
      >
        <button
          class="text-xs text-n-brand hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isLoading"
          @click="loadMore"
        >
          {{
            t('PIPELINE.FLOW_BUILDER.EXECUTION_HISTORY.LOAD_MORE', {
              count: meta.total - executions.length,
            })
          }}
        </button>
      </div>
    </div>
  </div>
</template>
